import { useState } from "react";
import { ChevronLeft, ChevronRight, Download, Presentation } from "lucide-react";
import {
  canvaEmbedUrl,
  deckHasSource,
  kindLabel,
  pdfViewerUrl,
  type OutlineTarget,
  type SlideDeck,
  type SlideMode,
} from "@/lib/slideDecks";

function mapCaption(deck: SlideDeck, targets: OutlineTarget[]) {
  return deck.maps
    .filter((map) => map.targetId || map.targets?.length)
    .map((map) => {
      const fromTargets = (map.targets || [])
        .map((pick) => pick.label)
        .filter(Boolean)
        .join(" · ");
      const label = fromTargets
        || map.targetLabel
        || targets.find((row) => row.id === map.targetId && row.kind === map.targetKind)?.label
        || "Outline";
      return { ...map, label };
    });
}

function PdfSlides({
  url,
  title,
  total,
  startPage,
}: {
  url: string;
  title: string;
  total?: number;
  startPage?: number;
}) {
  const first = Math.max(1, startPage || 1);
  const [page, setPage] = useState(first);
  const max = total && total > 0 ? total : undefined;
  const atStart = page <= 1;
  const atEnd = max != null ? page >= max : false;

  return (
    <div className="student-deck-pager-wrap">
      <div className="student-deck-pager">
        <button
          type="button"
          className="student-deck-pager-btn"
          disabled={atStart}
          aria-label="Previous slide"
          onClick={() => setPage((n) => Math.max(1, n - 1))}
        >
          <ChevronLeft size={16} aria-hidden />
        </button>
        <span>
          Slide {page}
          {max ? ` of ${max}` : ""}
        </span>
        <button
          type="button"
          className="student-deck-pager-btn"
          disabled={atEnd}
          aria-label="Next slide"
          onClick={() => setPage((n) => (max ? Math.min(max, n + 1) : n + 1))}
        >
          <ChevronRight size={16} aria-hidden />
        </button>
      </div>
      <div className="student-body-embed student-deck-pdf">
        <iframe key={page} title={title} src={pdfViewerUrl(url, page)} />
      </div>
    </div>
  );
}

export default function StudentSlideDecks({
  decks,
  targets = [],
  mode = null,
  heading = true,
}: {
  decks: SlideDeck[];
  targets?: OutlineTarget[];
  mode?: SlideMode | null;
  heading?: boolean;
}) {
  const filled = decks.filter((deck) => deckHasSource(deck) || deck.title.trim() || deck.maps.length);
  if (!filled.length) return null;
  const mapped = mode !== "activity";
  const title = mapped && mode === "map" ? "Thematic study" : "Slides";
  const showTitle = heading || title !== "Slides";

  return (
    <div className="student-section">
      {showTitle ? (
        <div className="student-section__head">
          <h2>
            <Presentation size={16} aria-hidden /> {title}
          </h2>
        </div>
      ) : null}
      {filled.map((deck) => {
        const captions = mapped ? mapCaption(deck, targets) : [];
        const embed = deck.kind === "canva" ? canvaEmbedUrl(deck.url) : "";
        return (
          <div key={deck.id} className="student-card student-deck">
            <strong>{deck.title.trim() || "Slide study"}</strong>
            {deck.slideCount ? (
              <p className="student-muted">
                {kindLabel(deck.kind === "pptx" ? "pdf" : deck.kind)} · {deck.slideCount} slides
              </p>
            ) : (
              <p className="student-muted">{kindLabel(deck.kind === "pptx" ? "pdf" : deck.kind)}</p>
            )}

            {embed ? (
              <div className="student-body-embed">
                <iframe title={deck.title || "Canva"} src={embed} allowFullScreen />
              </div>
            ) : deck.kind === "pdf" && deck.url ? (
              <PdfSlides
                url={deck.url}
                title={deck.title || "PDF"}
                total={deck.slideCount}
                startPage={captions[0]?.from || 1}
              />
            ) : deck.kind === "pptx" && deck.url ? (
              <p>
                <a className="student-text-link" href={deck.url} target="_blank" rel="noreferrer">
                  <Download size={14} aria-hidden /> Download file
                </a>
              </p>
            ) : null}

            {captions.length ? (
              <ul className="student-deck-maps">
                {captions.map((map) => (
                  <li key={map.id}>
                    {deck.kind === "pdf" && deck.url ? (
                      <a href={pdfViewerUrl(deck.url, map.from)} target="_blank" rel="noreferrer">
                        Slides {map.from}–{map.to}
                      </a>
                    ) : (
                      <span>
                        Slides {map.from}–{map.to}
                      </span>
                    )}
                    <span className="student-muted"> · {map.label}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
